import React, { useState } from 'react';
import styled from 'styled-components';
import TableFilter from 'components/TableFilter';

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const DemoCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
`;

const DemoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const DemoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  margin: 0;
`;

const FilterValues = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.3)' : '#f8fafc'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  padding: 16px;
  font-family: monospace;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  white-space: pre-wrap;
`;

const CodeBlock = styled.pre`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.4)' : '#1e293b'};
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto;
  margin: 0 0 24px 0;
  font-size: 13px;
  line-height: 1.6;
  color: #e2e8f0;
`;

const PropsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  font-size: 14px;

  th,
  td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid
      ${(props) =>
        props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  }

  th {
    font-weight: 600;
    color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc'};
  }

  td {
    color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  }

  code {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`;

const TableFilterDocs = () => {
  // Select filter demo
  const [selectFilters, setSelectFilters] = useState({ status: '' });
  const selectFilterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'All Statuses',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Pending', value: 'pending' },
      ],
    },
  ];

  // Radio filter demo
  const [radioFilters, setRadioFilters] = useState({ priority: '' });
  const radioFilterConfig = [
    {
      key: 'priority',
      label: 'Priority',
      type: 'radio',
      options: [
        { label: 'High', value: 'high' },
        { label: 'Medium', value: 'medium' },
        { label: 'Low', value: 'low' },
      ],
    },
  ];

  // Checkbox filter demo
  const [checkboxFilters, setCheckboxFilters] = useState({ categories: [] });
  const checkboxFilterConfig = [
    {
      key: 'categories',
      label: 'Categories',
      type: 'checkbox',
      options: [
        { label: 'Electronics', value: 'electronics' },
        { label: 'Clothing', value: 'clothing' },
        { label: 'Food', value: 'food' },
        { label: 'Books', value: 'books' },
      ],
    },
  ];

  // Combined filter demo
  const [combinedFilters, setCombinedFilters] = useState({
    status: '',
    type: '',
    tags: [],
  });
  const combinedFilterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'All',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
      ],
    },
    {
      key: 'type',
      label: 'Type',
      type: 'radio',
      options: [
        { label: 'Standard', value: 'standard' },
        { label: 'Premium', value: 'premium' },
        { label: 'Enterprise', value: 'enterprise' },
      ],
    },
    {
      key: 'tags',
      label: 'Tags',
      type: 'checkbox',
      options: [
        { label: 'Featured', value: 'featured' },
        { label: 'New', value: 'new' },
        { label: 'Sale', value: 'sale' },
      ],
    },
  ];

  return (
    <div>
      <PageTitle>Table Filter</PageTitle>
      <PageDescription>
        A reusable filter component for tables that supports dropdown selects,
        radio buttons (single selection), and checkboxes (multiple selection).
        Includes mobile-friendly bottom sheet behavior.
      </PageDescription>

      {/* Basic Usage */}
      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <SectionDescription>
          Import the TableFilter component and provide a filter configuration
          array along with state handlers.
        </SectionDescription>
        <CodeBlock>
          {`import TableFilter from 'components/TableFilter';

const [filters, setFilters] = useState({ status: '' });

const filterConfig = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    placeholder: 'All Statuses',
    options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ],
  },
];

<TableFilter
  filters={filterConfig}
  values={filters}
  onApply={(newFilters) => setFilters(newFilters)}
  onClear={() => setFilters({ status: '' })}
/>`}
        </CodeBlock>
      </Section>

      {/* Select Filter */}
      <Section>
        <SectionTitle>Select Filter (Dropdown)</SectionTitle>
        <SectionDescription>
          Use type &quot;select&quot; for a dropdown filter. Best for when you
          want users to select a single value from a predefined list.
        </SectionDescription>
        <DemoCard>
          <DemoHeader>
            <DemoTitle>Select Filter Demo</DemoTitle>
            <TableFilter
              filters={selectFilterConfig}
              values={selectFilters}
              onApply={(newFilters) => setSelectFilters(newFilters)}
              onClear={() => setSelectFilters({ status: '' })}
            />
          </DemoHeader>
          <FilterValues>
            Applied filters: {JSON.stringify(selectFilters, null, 2)}
          </FilterValues>
        </DemoCard>
      </Section>

      {/* Radio Filter */}
      <Section>
        <SectionTitle>Radio Filter (Single Selection)</SectionTitle>
        <SectionDescription>
          Use type &quot;radio&quot; for single selection with visual feedback.
          Clicking the same option again will deselect it.
        </SectionDescription>
        <DemoCard>
          <DemoHeader>
            <DemoTitle>Radio Filter Demo</DemoTitle>
            <TableFilter
              filters={radioFilterConfig}
              values={radioFilters}
              onApply={(newFilters) => setRadioFilters(newFilters)}
              onClear={() => setRadioFilters({ priority: '' })}
            />
          </DemoHeader>
          <FilterValues>
            Applied filters: {JSON.stringify(radioFilters, null, 2)}
          </FilterValues>
        </DemoCard>
      </Section>

      {/* Checkbox Filter */}
      <Section>
        <SectionTitle>Checkbox Filter (Multiple Selection)</SectionTitle>
        <SectionDescription>
          Use type &quot;checkbox&quot; for multiple selection. Selected values
          are stored as an array.
        </SectionDescription>
        <DemoCard>
          <DemoHeader>
            <DemoTitle>Checkbox Filter Demo</DemoTitle>
            <TableFilter
              filters={checkboxFilterConfig}
              values={checkboxFilters}
              onApply={(newFilters) => setCheckboxFilters(newFilters)}
              onClear={() => setCheckboxFilters({ categories: [] })}
            />
          </DemoHeader>
          <FilterValues>
            Applied filters: {JSON.stringify(checkboxFilters, null, 2)}
          </FilterValues>
        </DemoCard>
      </Section>

      {/* Combined Filters */}
      <Section>
        <SectionTitle>Combined Filters</SectionTitle>
        <SectionDescription>
          Combine multiple filter types in a single dropdown for more complex
          filtering needs.
        </SectionDescription>
        <DemoCard>
          <DemoHeader>
            <DemoTitle>Combined Filter Demo</DemoTitle>
            <TableFilter
              filters={combinedFilterConfig}
              values={combinedFilters}
              onApply={(newFilters) => setCombinedFilters(newFilters)}
              onClear={() =>
                setCombinedFilters({ status: '', type: '', tags: [] })
              }
            />
          </DemoHeader>
          <FilterValues>
            Applied filters: {JSON.stringify(combinedFilters, null, 2)}
          </FilterValues>
        </DemoCard>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable>
          <thead>
            <tr>
              <th>Prop</th>
              <th>Type</th>
              <th>Default</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>filters</code>
              </td>
              <td>
                <code>Array</code>
              </td>
              <td>
                <code>[]</code>
              </td>
              <td>Array of filter configuration objects (required)</td>
            </tr>
            <tr>
              <td>
                <code>values</code>
              </td>
              <td>
                <code>Object</code>
              </td>
              <td>
                <code>{'{}'}</code>
              </td>
              <td>Current filter values keyed by filter key</td>
            </tr>
            <tr>
              <td>
                <code>onChange</code>
              </td>
              <td>
                <code>Function</code>
              </td>
              <td>-</td>
              <td>Called when any filter value changes (before apply)</td>
            </tr>
            <tr>
              <td>
                <code>onApply</code>
              </td>
              <td>
                <code>Function</code>
              </td>
              <td>-</td>
              <td>Called when Apply Filters button is clicked</td>
            </tr>
            <tr>
              <td>
                <code>onClear</code>
              </td>
              <td>
                <code>Function</code>
              </td>
              <td>-</td>
              <td>Called when Clear button is clicked</td>
            </tr>
            <tr>
              <td>
                <code>showButton</code>
              </td>
              <td>
                <code>boolean</code>
              </td>
              <td>
                <code>true</code>
              </td>
              <td>Whether to show the filter toggle button</td>
            </tr>
            <tr>
              <td>
                <code>isOpen</code>
              </td>
              <td>
                <code>boolean</code>
              </td>
              <td>-</td>
              <td>Controlled open state (optional)</td>
            </tr>
            <tr>
              <td>
                <code>onToggle</code>
              </td>
              <td>
                <code>Function</code>
              </td>
              <td>-</td>
              <td>Called when filter is toggled (for controlled mode)</td>
            </tr>
          </tbody>
        </PropsTable>
      </Section>

      {/* Filter Configuration */}
      <Section>
        <SectionTitle>Filter Configuration</SectionTitle>
        <SectionDescription>
          Each filter in the filters array should have the following structure:
        </SectionDescription>
        <PropsTable>
          <thead>
            <tr>
              <th>Property</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>key</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>Unique identifier for the filter (required)</td>
            </tr>
            <tr>
              <td>
                <code>label</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>Display label for the filter section (required)</td>
            </tr>
            <tr>
              <td>
                <code>type</code>
              </td>
              <td>
                <code>
                  &apos;select&apos; | &apos;radio&apos; | &apos;checkbox&apos;
                </code>
              </td>
              <td>Type of filter input (required)</td>
            </tr>
            <tr>
              <td>
                <code>options</code>
              </td>
              <td>
                <code>Array&lt;{'{label, value}'}&gt;</code>
              </td>
              <td>Array of options for the filter (required)</td>
            </tr>
            <tr>
              <td>
                <code>placeholder</code>
              </td>
              <td>
                <code>string</code>
              </td>
              <td>Placeholder text for select type (optional)</td>
            </tr>
          </tbody>
        </PropsTable>
      </Section>

      {/* Usage Example */}
      <Section>
        <SectionTitle>Full Example</SectionTitle>
        <CodeBlock>
          {`import React, { useState } from 'react';
import TableFilter from 'components/TableFilter';

const WaiversPage = () => {
  const [filters, setFilters] = useState({
    status: '',
    company: '',
  });

  const filterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'radio',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
      ],
    },
    {
      key: 'company',
      label: 'Insurance Company',
      type: 'select',
      placeholder: 'All Companies',
      options: companies.map(c => ({ label: c, value: c })),
    },
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    // Trigger data refetch with new filters
    refetchData({ ...queryParams, ...newFilters });
  };

  const handleClearFilters = () => {
    setFilters({ status: '', company: '' });
    refetchData(queryParams);
  };

  return (
    <PageHeader>
      <SearchInput onSearch={handleSearch} />
      <TableFilter
        filters={filterConfig}
        values={filters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />
      <ButtonV2 onClick={handleCreate}>Create Waiver</ButtonV2>
    </PageHeader>
  );
};`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default TableFilterDocs;
